/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { forwardRef, useEffect, useState } from "react";

import { useWindowSize } from "@react-hook/window-size";
import { isElectron } from "react-device-detect";

import { createImage } from "src/lib/utils/images";
interface Props {
  invite: { mnemonic: string; expiration: Date | null };
}

export const INVITE_CARD_WIDTH = 339;

export const InviteCard = forwardRef<any, Props>(({ invite }, ref) => {
  const [baseImage, setBaseImage] = useState<HTMLImageElement | null>(null);
  const [, setInviteCardImage] = useState<any>(); // unused for now, see comment on <img/> tag
  const [windowWidth] = useWindowSize();
  const inviteCardWidth = windowWidth <= 530 ? windowWidth - 100 : INVITE_CARD_WIDTH;

  useEffect(() => {
    // load image
    const img = createImage(isElectron ? "Invite-card-ns.png" : "/Invite-card-ns.png");

    img.onload = () => {
      setBaseImage(img);
    };
  }, []);

  useEffect(() => {
    if ((ref as any).current && baseImage) {
      // canvas method
      const canvas = (ref as any).current;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        const baseImageAspectRatio = baseImage.width / baseImage.height;
        // https://www.kirupa.com/canvas/canvas_high_dpi_retina.htm
        // the approach is to downsample: (1) scale up width and height of canvas to get better image and then (2) scale down using CSS.
        // get current size of the canvas
        const rect = canvas.getBoundingClientRect(); // to read the width/height properties we initially set on the canvas

        // we're going to increase the size of the canvas by this much (before scaling down with css)
        const increaseCanvasSizeByFactorOf = window.devicePixelRatio * 2;

        // (1) juice the canvas size
        canvas.width = rect.width * increaseCanvasSizeByFactorOf;
        canvas.height = rect.height * increaseCanvasSizeByFactorOf;

        // scale all drawing operations, see https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/scale
        ctx.scale(increaseCanvasSizeByFactorOf, increaseCanvasSizeByFactorOf);

        // (2) scale back down using CSS
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;
        canvas.style.objectFit = "contain";

        // transparent background
        ctx.fillStyle = "transparent";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.imageSmoothingEnabled = false;
        // args: image, x, y, width, height. height comes from aspect ratio of base image
        ctx.drawImage(baseImage, 0, 0, inviteCardWidth, inviteCardWidth / baseImageAspectRatio);

        // this isn't doing anything, unclear if will work with png shape
        ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
        ctx.shadowBlur = 10;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;

        //text section- code
        ctx.font = "14px IBM Plex Mono";
        ctx.fillStyle = "white";
        ctx.textAlign = "center";
        // text position derived from size of baseImage card
        ctx.fillText(invite.mnemonic, inviteCardWidth / 2, inviteCardWidth / (baseImageAspectRatio * 2) + 10);

        //text section - expiration
        if (invite.expiration) {
          const expirationDate = new Date(invite.expiration);
          const expirationMonth = expirationDate.getUTCMonth() + 1;
          const expirationDay = expirationDate.getUTCDate();
          const readableDate = `${expirationMonth}/${expirationDay}`;

          ctx.font = "8px IBM Plex Mono";
          ctx.fillStyle = "gray";
          ctx.textAlign = "center";
          ctx.fillText(
            `valid thru ${readableDate}`,
            inviteCardWidth / 2,
            inviteCardWidth / (baseImageAspectRatio * 2) + 23
          );
        }

        // hide the canvas, we will render an image instead
        // canvas.style.display = "none";
        setInviteCardImage(canvas.toDataURL("image/png"));
      }
    }
  }, [baseImage, ref, inviteCardWidth]);

  return (
    baseImage && (
      <canvas
        id="canvas"
        ref={ref}
        width={inviteCardWidth}
        height={(inviteCardWidth * baseImage.height) / baseImage.width}
      />
      //        gonna comment out the image thing for now, will bring back if need for mobile safari
      //  <img src={inviteCardImage} style={{ width: INVITE_CARD_WIDTH }} />
    )
  );
});
